import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  Trash2,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Plus,
} from 'lucide-react';
import clsx from 'clsx';
import { fetchGenerations, downloadGeneration, deleteGeneration } from '../lib/api';
import type { Generation } from '../types/wizard';

const statusConfig = {
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-gray-400',
    bg: 'bg-gray-500/10 border-gray-500/20',
  },
  generating: {
    label: 'Generating',
    icon: Loader2,
    color: 'text-accent',
    bg: 'bg-accent/10 border-accent/20',
    animate: true,
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    color: 'text-green-400',
    bg: 'bg-green-500/10 border-green-500/20',
  },
  failed: {
    label: 'Failed',
    icon: AlertCircle,
    color: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/20',
  },
};

// Demo generations
const demoGenerations: Generation[] = [
  {
    id: 'gen-1',
    name: 'Acme Platform',
    status: 'completed',
    createdAt: '2024-11-20T14:30:00Z',
    completedAt: '2024-11-20T14:31:15Z',
    projectContext: {
      name: 'acme-platform',
      goal: 'Greenfield Project',
      description: 'E-commerce platform with Next.js and TypeScript',
      industry: 'E-commerce / Retail',
      compliance: ['SOC 2', 'PCI DSS'],
    },
    wizardData: {} as Generation['wizardData'],
  },
  {
    id: 'gen-2',
    name: 'Internal Dashboard',
    status: 'completed',
    createdAt: '2024-11-18T10:15:00Z',
    completedAt: '2024-11-18T10:16:30Z',
    projectContext: {
      name: 'admin-dashboard',
      goal: 'Legacy Modernization',
      description: 'Internal admin dashboard rebuilt with React',
      industry: 'Technology / SaaS',
      compliance: ['SOC 2'],
    },
    wizardData: {} as Generation['wizardData'],
  },
  {
    id: 'gen-3',
    name: 'ML Pipeline Service',
    status: 'generating',
    createdAt: '2024-11-21T09:00:00Z',
    projectContext: {
      name: 'ml-pipeline',
      goal: 'Data Pipeline',
      description: 'Machine learning pipeline for recommendation engine',
      industry: 'Technology / SaaS',
      compliance: [],
    },
    wizardData: {} as Generation['wizardData'],
  },
  {
    id: 'gen-4',
    name: 'Auth Service',
    status: 'failed',
    createdAt: '2024-11-17T16:45:00Z',
    error: 'Template validation failed: Missing required field "cloudProvider"',
    projectContext: {
      name: 'auth-service',
      goal: 'Microservices Migration',
      description: 'Authentication microservice',
      industry: 'Financial Services / Fintech',
      compliance: ['SOC 2', 'PCI DSS'],
    },
    wizardData: {} as Generation['wizardData'],
  },
];

export default function GenerationsPage() {
  const [generations, setGenerations] = useState<Generation[]>(demoGenerations);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadGenerations = async () => {
      try {
        setLoading(true);
        const data = await fetchGenerations();
        // Use API response (even if empty) — demo data is only for errors
        setGenerations(data ?? []);
      } catch {
        // Use demo data as fallback when API is unreachable
      } finally {
        setLoading(false);
      }
    };
    loadGenerations();
  }, []);

  const handleDownload = async (id: string) => {
    try {
      const blob = await downloadGeneration(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `codeforge-${id}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Download failed
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGeneration(id);
      setGenerations((prev) => prev.filter((g) => g.id !== id));
    } catch {
      // Still remove from UI for demo
      setGenerations((prev) => prev.filter((g) => g.id !== id));
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Your Generations
            </h1>
            <p className="text-gray-400">
              Generated blueprints for your projects.
            </p>
          </div>
          <Link to="/wizard" className="btn-primary">
            <Plus className="h-4 w-4" />
            New Generation
          </Link>
        </div>

        {/* Generations List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card animate-pulse h-32" />
            ))}
          </div>
        ) : generations.length === 0 ? (
          <div className="text-center py-20 card">
            <p className="text-gray-500 text-lg mb-2">
              No generations yet
            </p>
            <p className="text-gray-600 text-sm mb-6">
              Use the wizard to create your first blueprint.
            </p>
            <Link to="/wizard" className="btn-primary">
              <Plus className="h-4 w-4" />
              Create Your First Generation
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {generations.map((gen) => {
              const status = statusConfig[gen.status];
              const StatusIcon = status.icon;

              return (
                <div key={gen.id} className="card card-hover">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <Link
                          to={`/generations/${gen.id}`}
                          className="text-lg font-semibold text-white hover:text-accent transition-colors truncate"
                        >
                          {gen.name}
                        </Link>
                        <span
                          className={clsx(
                            'badge border shrink-0',
                            status.bg,
                            status.color
                          )}
                        >
                          <StatusIcon
                            className={clsx(
                              'h-3 w-3 mr-1',
                              (status as { animate?: boolean }).animate && 'animate-spin'
                            )}
                          />
                          {status.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                        <span>{gen.projectContext.goal}</span>
                        <span>&middot;</span>
                        <span>{gen.projectContext.industry}</span>
                        <span>&middot;</span>
                        <span>
                          {new Date(gen.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {gen.projectContext.compliance.length > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          {gen.projectContext.compliance.map((c) => (
                            <span
                              key={c}
                              className="px-2 py-0.5 text-xs rounded bg-surface-100 text-gray-400 border border-dark-border"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                      {gen.error && (
                        <p className="text-sm text-red-400 mt-2">
                          {gen.error}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {gen.status === 'completed' && (
                        <>
                          <Link
                            to={`/generations/${gen.id}`}
                            className="btn-secondary text-sm py-2 px-4"
                          >
                            View Files
                          </Link>
                          <button
                            onClick={() => handleDownload(gen.id)}
                            className="btn-ghost text-sm py-2 px-3"
                            title="Download ZIP"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {gen.status === 'generating' && (
                        <Link
                          to={`/generations/${gen.id}`}
                          className="btn-secondary text-sm py-2 px-4"
                        >
                          View Progress
                        </Link>
                      )}
                      <button
                        onClick={() => handleDelete(gen.id)}
                        className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
